import React from 'react';
import { Stack } from '@fluentui/react';
import { useStyles } from 'common/components/ActionMenu/Tags/index.styles';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';
import { EntityTagInput } from 'common/types/globalTypes';
import {
  TravelAuthorizationAvailableTags,
  TravelAuthorizationAvailableTagsVariables,
} from 'common/graphql/__generated__/TravelAuthorizationAvailableTags';
import {
  ListTravelAuthorizationTags,
  ListTravelAuthorizationTagsVariables,
} from 'common/graphql/__generated__/ListTravelAuthorizationTags';
import {
  EntityTagTravelAuthorizationCreate,
  EntityTagTravelAuthorizationCreateVariables,
} from 'common/graphql/__generated__/EntityTagTravelAuthorizationCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';

import ENTITY_TRAVEL_AUTHORIZATION_CREATE from 'common/graphql/EntityTagTravelAuthorizationCreate.graphql';
import TRAVEL_AUTHORIZATION_AVAILABLE_TAGS from 'common/graphql/TravelAuthorizationAvailableTags.graphql';
import LIST_TRAVEL_AUTHORIZATION_TAGS from 'common/graphql/ListTravelAuthorizationTags.graphql';
import ENTITY_TAG_DELETE from 'common/graphql/EntityTagDelete.graphql';

interface TagsProps {
  travelAuthorizationId: string;
  dirty: boolean;
}

export const Tags: React.FC<TagsProps> = ({ travelAuthorizationId, dirty }) => {
  const styles = useStyles();

  // Memoized functions for extracting data and generating variables
  const extractAvailableTags = React.useCallback(
    (data: TravelAuthorizationAvailableTags | undefined) =>
      data?.travelAuthorizationAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? [],
    []
  );

  const extractEntityTags = React.useCallback(
    (data: ListTravelAuthorizationTags | undefined) =>
      data?.travelAuthorization?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown',
      })) ?? [],
    []
  );

  const getAvailableTagsVars = React.useCallback(
    (entityId: string): TravelAuthorizationAvailableTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const getEntityTagsVars = React.useCallback(
    (entityId: string): ListTravelAuthorizationTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const createTagsVars = React.useCallback(
    (tags: EntityTagInput[]): EntityTagTravelAuthorizationCreateVariables => ({
      input: {
        entityTag: tags,
      },
    }),
    []
  );

  const deleteTagsVars = React.useCallback(
    (id: string, rowTimestamp: string): EntityTagDeleteVariables => ({
      entityTagDeleteInput: {
        entityDelete: [{ id, rowTimestamp }],
      },
    }),
    []
  );

  return (
    <Stack tokens={{ childrenGap: 15, padding: '24px 25px 0px' }}>
      <EntityTagsSection<
        TravelAuthorizationAvailableTags,
        TravelAuthorizationAvailableTagsVariables,
        ListTravelAuthorizationTags,
        ListTravelAuthorizationTagsVariables,
        EntityTagTravelAuthorizationCreate,
        EntityTagTravelAuthorizationCreateVariables,
        EntityTagDelete,
        EntityTagDeleteVariables
      >
        entityId={travelAuthorizationId}
        getAvailableTagsGql={TRAVEL_AUTHORIZATION_AVAILABLE_TAGS}
        getEntityTagsGql={LIST_TRAVEL_AUTHORIZATION_TAGS}
        addTagGql={ENTITY_TRAVEL_AUTHORIZATION_CREATE}
        deleteTagGql={ENTITY_TAG_DELETE}
        extractAvailableTags={extractAvailableTags}
        extractEntityTags={extractEntityTags}
        getAvailableTagsVars={getAvailableTagsVars}
        getEntityTagsVars={getEntityTagsVars}
        createTagsVars={createTagsVars}
        deleteTagsVars={deleteTagsVars}
        isDisabled={dirty}
      />
    </Stack>
  );
};
